"use client"

import { useEffect, useState, useCallback } from 'react'
import { useIsStandalone } from './use-is-standalone'

type Platform = 'ios' | 'android' | 'desktop' | 'unknown'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function useInstallPrompt() {
  const isStandalone = useIsStandalone()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [platform, setPlatform] = useState<Platform>('unknown')
  const [isSafari, setIsSafari] = useState(false)

  // Детект платформы + браузера
  useEffect(() => {
    if (typeof navigator === 'undefined') return
    const ua = navigator.userAgent.toLowerCase()

    // iPad на iOS 13+ маскируется под Mac — проверяем touch
    const isIOS =
      /iphone|ipad|ipod/.test(ua) ||
      (/macintosh/.test(ua) && navigator.maxTouchPoints > 1)

    if (isIOS) setPlatform('ios')
    else if (/android/.test(ua)) setPlatform('android')
    else setPlatform('desktop')

    // Safari (не Chrome и не Firefox на iOS всё равно Safari-движок, но detect для UI)
    const safari = /^((?!chrome|android|crios|fxios|edgios).)*safari/.test(ua)
    setIsSafari(safari)
  }, [])

  // Слушаем beforeinstallprompt
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // Если установили — очищаем prompt
    const installed = () => setDeferredPrompt(null)
    window.addEventListener('appinstalled', installed)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installed)
    }
  }, [])

  // Показать нативный prompt (только Android/desktop Chrome)
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    // Аналитика
    if (typeof window !== 'undefined') {
      const w = window as any
      const props = {
        outcome,
        platform: /android/i.test(navigator.userAgent) ? 'android' : 'desktop',
      }
      try { w.posthog?.capture?.('pwa_install_prompt_response', props) } catch {}
      try { w.plausible?.('pwa_install_prompt_response', { props }) } catch {}
      try { w.umami?.track?.('pwa_install_prompt_response', props) } catch {}
    }

    setDeferredPrompt(null)
    return outcome
  }, [deferredPrompt])

  return {
    isStandalone,
    platform,
    isSafari,
    // На iOS нативного prompt нет — показываем инструкцию (canShowIosGuide)
    canShowIosGuide: platform === 'ios' && !isStandalone,
    // На Android/desktop prompt появляется если Chrome посчитал сайт eligible
    canPromptInstall: !!deferredPrompt && !isStandalone,
    promptInstall,
  }
}